import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Data from 'data';
import Tooltip from 'ui/tooltip';
import { itemAllowed, itemSlotValid } from 'logic/item';
import { D3TooltipCube, D3TooltipFollowerSkill, D3TooltipItem, D3TooltipSkill } from 'tooltips';

import './Paperdoll.scss';
import EditingContext from 'store/editingContext';
import { cubeItemAllowed, cubeSlotValid, cubeTypes } from 'logic/cube';
import { useDragDrop, useItemMover, useSkillMover, usePassiveMover } from 'components/dragDrop';
import { getAttackType } from 'logic/skills';
import { chainedRef } from 'utils';
import { OverlayContext } from 'ui/utils/OverlayManager';
import Locale from 'data/locale';
import AutoSave from './AutoSave';
import { getIconPath } from 'common';

const _L = Locale("ui-equipment.js");

function getItemIconEx(slot, profile, follower) {
  const data = (follower ? profile.followerItems[slot] : profile.items[slot]);
  if (data.id === "custom" || data.id === "customwpn") {
    const types = (slot === "offhand" ? Data.getOffhandTypes(profile, follower) : Data.itemSlots[slot].types);
    for (let type of Object.values(types)) {
      if (type.classes && type.classes.indexOf(profile.class) < 0) continue;
      if (data.id !== "customwpn" && type.weapon) continue;
      if (data.id === "customwpn" && !type.weapon) continue;
      return Data.getItemIcon(type.generic, profile);
    }
  }
  return Data.getItemIcon(data.id, profile);
}

/*function weaponElement(data) {
  for (let stat in data.stats) {
    if (Data.stats[stat].elemental) {
      return Data.stats[stat].elemental;
    }
  }
}*/

function WeaponFlourish({ slot, data }) {
  return null;
  /*const element = data && weaponElement(data);
  if (!element || !Data.itemById[data.id]) {
    return null;
  }
  return <span className={`d3p-WeaponFlourish d3p-flourish-${slot} d3p-elemental-${element}`}/>;*/
}

function GemImage({ gem }) {
  if (!Array.isArray(gem)) {
    if (gem && Data.itemById[gem.id]) {
      const path = Data.getItemIcon(gem.id);
      if (path) return <img src={path} alt={Data.itemById[gem.id].name}/>;
    }
  }
  const [id, color] = gem;
  if (Data.legendaryGems[id]) {
    const path = Data.getItemIcon(id);
    if (path) return <img src={path} alt={Data.legendaryGems[id].name}/>;
  } else if (Data.gemQualities[id] && Data.gemColors[color]) {
    const path = Data.getItemIcon(gem);
    if (path) return <img src={path} alt={Data.gemColors[color].names[id]}/>;
  }
  return null;
}

function PaperdollSlot({ profile, slot, follower }) {
  const overlays = React.useContext(OverlayContext);
  const activeSlot = EditingContext.useStateView("itemSlotEditing");
  const highlight = EditingContext.useStateView("highlightItems");
  const editDispatch = EditingContext.useDispatch();
  const onClick = React.useCallback(() => editDispatch({type: "item", slot}), [editDispatch, slot]);
  const dispatch = Store.useDispatch();

  const itemMover = useItemMover();

  const slotData = Data.itemSlots[slot];
  const itemData = (follower ? profile.followerItems[slot] : profile.items[slot]);
  const item = itemData && Data.itemById[itemData.id];

  const [dragClass, drag, drop] = useDragDrop("ITEM", {
    canDrag: () => item != null,
    begin: () => ({type: "ITEM", id: item.id}),
    end: (item, monitor) => itemMover({location: "equipment", slot}, monitor.getDropResult()),
  }, {
    canDrop: item => item.type === "ITEM" && itemAllowed(profile, slot, item.id, follower),
    drop: () => ({location: "equipment", slot}),
  });
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  const onContextMenu = React.useCallback(e => {
    if (activeSlot && item) {
      e.preventDefault();
      overlays.menu(e, [_L`Delete`]).then(result => {
        if (result === 0) {
          if (follower) {
            dispatch.profile.setFollowerItem(slot, null);
          } else {
            dispatch.profile.setItem(slot, null);
          }
        }
      });
    }
  }, [overlays, dispatch, slot, activeSlot, item, follower]);

  if (!itemSlotValid(follower ? profile.follower : profile.class, slot)) return null;

  let disabledImage = null;
  let className = `d3p-DollSlot d3p-DragDrop d3p-slot-${slot}`;
  if (!highlight && slot === activeSlot) {
    className += " d3p-editing";
  }
  if (highlight && highlight[slot]) {
    className += " d3p-highlight";
  }
  if (!item) {
    className += " d3p-empty";
    if (!itemData && slot === "offhand") {
      const mhData = (follower ? profile.followerItems : profile.items).mainhand;
      const mhItem = mhData && Data.itemById[mhData.id];
      if (mhItem && Data.itemTypes[mhItem.type].slot === "twohand") {
        if (!follower && profile.class === "demonhunter" && ["bow", "crossbow"].includes(mhItem.type)) {
          className += " d3p-slot-quiver";
        } else {
          className += " d3p-disabled-offhand";
          disabledImage = <img src={getItemIconEx("mainhand", profile, follower)} alt={mhItem.name}/>;
        }
      }
    }
  }

  if (item && highlight && !highlight[slot] && highlight[itemData.id]) {
    className += " d3p-highlight";
  }

  if (item) {
    if (itemData.ancient === "sanctified") {
      className += ` d3p-quality-sanctified`;
    } else {
      className += ` d3p-quality-${item.quality}`;
      if (itemData.ancient === "primal") {
        className += " d3p-primal";
      }
    }
  }

  if (dragClass) {
    className += " " + dragClass;
  }

  const node = (
    <div ref={nodeRef} className={className} onClick={onClick} onContextMenu={onContextMenu}>
      {disabledImage}
      {!!item && <span className="d3p-ItemGradient"><span className="d3p-ItemGlow"/></span>}
      {!!item && <img src={getItemIconEx(slot, profile, follower)} alt={item.name}/>}
      {!!(item && slotData.sockets) && (
        <span className="d3p-SocketsWrapper">
          <span className="d3p-SocketsAlign">
            {!!itemData.gems && itemData.gems.map((gem, idx) => {
              return (
                <React.Fragment key={idx}>
                  <span className="d3p-Socket">
                    {!!gem && <GemImage gem={gem}/>}
                  </span>
                  <br/>
                </React.Fragment>
              );
            })}
          </span>
        </span>
      )}
    </div>
  );

  if (item) {
    return (
      <D3TooltipItem key={slot} slot={slot} follower={follower} disabled={dragClass === "d3p-dragging"}>
        {node}
      </D3TooltipItem>
    );
  } else if (!disabledImage) {
    return (
      <Tooltip key={slot} tooltip={slotData.name} align="top">
        {node}
      </Tooltip>
    );
  } else {
    return node;
  }
}

function PaperdollGender() {
  const dispatch = Store.useDispatch();
  const gender = Store.useProfile(profile => profile.gender);

  const setMale = React.useCallback(() => dispatch.profile.setGender("male"), [dispatch]);
  const setFemale = React.useCallback(() => dispatch.profile.setGender("female"), [dispatch]);

  return (
    <React.Fragment>
      <span className={classNames("d3p-gender-switch d3p-male", {"d3p-active": gender === "male"})} onClick={setMale}/>
      <span className={classNames("d3p-gender-switch d3p-female", {"d3p-active": gender === "female"})} onClick={setFemale}/>
    </React.Fragment>
  );
}

function PaperdollGear({ follower }) {
  const profile = Store.useProfile();
  const className = follower
    ? `d3p-paperdoll-background d3p-class-${profile.follower}`
    : `d3p-paperdoll-background d3p-class-${profile.class} d3p-${profile.gender}`;
  return (
    <div className={className}>
      {!follower && <PaperdollGender/>}
      {!follower && <WeaponFlourish slot="mainhand" data={profile.items.mainhand}/>}
      {!follower && <WeaponFlourish slot="offhand" data={profile.items.offhand}/>}
      {Object.keys(Data.itemSlots).filter(slot => slot !== 'follower').map(slot => <PaperdollSlot key={slot} profile={profile} slot={slot} follower={follower}/>)}
    </div>
  );
}

function PaperdollSkillSlot({ index }) {
  const attackType = Store.useProfile(getAttackType);
  const charClass = Store.useProfile(profile => profile.class);
  const skill = Store.useProfile(profile => profile.skills[index]);
  const dispatch = EditingContext.useDispatch();

  const info = skill && Data.skills[charClass][skill[0]];
  const onClick = React.useCallback(() => dispatch({type: "skill", index}), [dispatch, index]);

  const skillMover = useSkillMover();

  const [dragClass, drag, drop] = useDragDrop("SKILL", {
    canDrag: () => info != null,
    options: {dropEffect: "move"},
    begin: () => ({type: "SKILL", charClass, id: skill[0], index}),
    end: (item, monitor) => skillMover({index}, monitor.getDropResult()),
  }, {
    canDrop: item => {
      if (item.type !== "SKILL") return false;
      if (index === 0 && Data.skills[charClass][item.id]?.nolmb) return false;
      if (item.index === 0 && info?.nolmb) return false;
      return true;
    },
    drop: () => ({index}),
  });
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  let displayClass = charClass;
  let displayInfo = dragClass === "d3p-dragging" ? null : info;

  if (!displayInfo && (index === 0 || index === 1)) {
    displayClass = "attack";
    displayInfo = Data.skills.attack[attackType];
  }

  return (
    <D3TooltipSkill charClass={displayClass} skill={skill ? skill[0] : attackType} rune={skill?.[1]} disabled={!displayInfo || dragClass === "d3p-dragging"}>
      <div className={classNames(`d3p-skill-icon d3p-DragDrop d3p-class-${displayClass}`, displayClass === "attack" && `d3p-${displayInfo.id}`, dragClass, {"d3p-empty": !displayInfo})} onClick={onClick}
           style={(!displayInfo || displayClass === "attack") ? null : {backgroundPosition: `${-info.col}em ${-2 * info.row}em`}} ref={nodeRef}>
      </div>
    </D3TooltipSkill>
  );
}

function PaperdollSkills() {
  return (
    <div className="d3p-PaperdollSkills">
      {[0, 1, 2, 3, 4, 5].map(index => <PaperdollSkillSlot key={index} index={index}/>)}
    </div>
  )
}

const passiveLevels = [10, 20, 30, 70];

function PaperdollPassiveSlot({ index }) {
  const charClass = Store.useProfile(profile => profile.class);
  const passive = Store.useProfile(profile => profile.passives[index]);
  const dispatch = EditingContext.useDispatch();

  const info = passive && Data.passives[charClass][passive];
  const onClick = React.useCallback(() => dispatch({type: "passive", index}), [dispatch, index]);

  const passiveMover = usePassiveMover();

  const [dragClass, drag, drop] = useDragDrop("PASSIVE", {
    canDrag: () => info != null,
    options: {dropEffect: "move"},
    begin: () => ({type: "PASSIVE", charClass, id: passive}),
    end: (item, monitor) => passiveMover({index}, monitor.getDropResult()),
  }, {
    drop: () => ({index}),
  });
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  const empty = (!info || dragClass === "d3p-dragging");

  return (
    <D3TooltipSkill key={index} charClass={charClass} skill={passive} disabled={empty}>
      <div key={index} className={classNames(`d3p-passive-icon d3p-DragDrop d3p-class-${charClass}`, dragClass, {"d3p-empty": empty})} onClick={onClick}
          style={empty ? null : {backgroundPosition: `${-info.index}em 0`}} ref={nodeRef}>
        <span className="d3p-passive-frame d3p-fancy"/>
        {empty && <span className="d3p-passive-text">{passiveLevels[index]}</span>}
      </div>
    </D3TooltipSkill>
  );
}

function PaperdollPassives() {
  return (
    <div className="d3p-PaperdollPassives">
      {[0, 1, 2, 3].map(index => <PaperdollPassiveSlot key={index} index={index}/>)}
    </div>
  )
}

function PaperdollCubeSlot({ slot }) {
  const charClass = Store.useProfile(profile => profile.class);
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const itemId = Store.useProfile(profile => profile.kanai[slot]);
  const dispatch = EditingContext.useDispatch();

  const item = itemId && Data.itemById[itemId];

  const itemMover = useItemMover();

  const [dragClass, drag, drop] = useDragDrop("ITEM", {
    canDrag: () => item != null,
    begin: () => ({type: "ITEM", id: item.id}),
    end: (item, monitor) => itemMover({location: "cube", slot}, monitor.getDropResult()),
  }, {
    canDrop: item => item.type === "ITEM" && cubeItemAllowed({seasonal, class: charClass}, slot, item.id),
    drop: () => ({location: "cube", slot}),
  });
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  const onClick = React.useCallback(() => dispatch({type: "cube", slot}), [dispatch, slot]);

  if (!cubeSlotValid({seasonal}, slot)) return null;

  return (
    <D3TooltipCube id={item && item.id} disabled={dragClass === "d3p-dragging"}>
      <div className={classNames(`d3p-cube-item d3p-DragDrop d3p-cube-${slot}`, dragClass, {"d3p-empty": !item})} onClick={onClick} ref={nodeRef}>
        {!!item && <div className="d3p-item-icon" style={{backgroundImage: `url(${Data.getItemIcon(item.id)})`}}/>}
      </div>
    </D3TooltipCube>
  );
}

function PaperdollCube() {
  return (
    <div className="d3p-PaperdollCube">
      {Object.keys(cubeTypes).map(slot => <PaperdollCubeSlot key={slot} slot={slot}/>)}
    </div>
  )
}

function PaperdollFollowerSkill({ id, active, onSelect }) {
  const onClick = React.useCallback(() => onSelect(active ? null : id), [onSelect, active, id]);
  const skill = Data.followerSkills[id];
  if (!skill) return null;

  return (
    <D3TooltipFollowerSkill id={id}>
      <div className="d3p-follower-skill" onClick={onClick} style={{backgroundImage: `url(${getIconPath(active ? skill.icon : skill.icon_inactive)})`}}/>
    </D3TooltipFollowerSkill>
  );
}

function PaperdollFollowerSkillGroup({ index }) {
  const allSkills = Store.useProfile(profile => {
    const item = profile.followerItems?.follower;
    const info = item && Data.itemById[item.id];
    return info?.required?.custom?.id === "leg_followerallskills";
  });
  const follower = Store.useProfile(profile => profile.follower);
  const selected = Store.useProfile(profile => profile.followerSkills?.[index]);

  const skill1 = Data.followerSkillList[follower]?.[index * 2];
  const skill2 = Data.followerSkillList[follower]?.[index * 2 + 1];

  const dispatch = Store.useDispatch();
  const onSelect = React.useCallback(skill => {
    if (allSkills) return;
    dispatch.profile.setFollowerSkill(index, skill);
  }, [dispatch, index, allSkills]);

  return (
    <div className="d3p-follower-skill-group">
      <PaperdollFollowerSkill id={skill1} active={allSkills || skill1 === selected} onSelect={onSelect}/>
      <PaperdollFollowerSkill id={skill2} active={allSkills || skill2 === selected} onSelect={onSelect}/>
    </div>
  );
}

function PaperdollFollower() {
  const profile = Store.useProfile();
  if (!profile.follower) return null;

  return (
    <div className="d3p-PaperdollFollower">
      <div className="d3p-follower-relic">
        <PaperdollSlot profile={profile} slot="follower" follower={true}/>
      </div>
      <div className="d3p-follower-skills">
        <PaperdollFollowerSkillGroup index={0}/>
        <PaperdollFollowerSkillGroup index={1}/>
        <PaperdollFollowerSkillGroup index={2}/>
        <PaperdollFollowerSkillGroup index={3}/>
      </div>
    </div>
  );
}

export default React.memo(function Paperdoll() {
  const mode = EditingContext.useStateView("paperdollMode");
  const follower = (mode === "follower");
  return (
    <div className="d3p-PaperdollContainer">
      <PaperdollGear follower={follower}/>
      {!follower && <PaperdollSkills/>}
      {!follower && <PaperdollPassives/>}
      {!follower && <PaperdollCube/>}
      {follower && <PaperdollFollower/>}
      <AutoSave/>
    </div>
  );
});
